import { useState } from 'react'
import { Container, Box, Typography, TextField, Button, Alert } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '~/supabase-client'
import { useAuthContext } from '~/common/context/auth/auth-context'
import { useUpdateBilling } from '~/api/supabase-mutations'
import { createUserProfile } from './new-user-profile'
import { validateInviteTicket } from '~/invite-user/invite-ticket-utils'
import { TierSelection } from './tier-selection'

const isInviteMode = () => !!sessionStorage.getItem('pendingInviteTicket')

export const SetPassword = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [inviteWarning, setInviteWarning] = useState(null)
  const [step, setStep] = useState(1)
  const [resolvedUser, setResolvedUser] = useState(null)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const skipTier = searchParams.has('skipTier')
  const { user, markProfileComplete } = useAuthContext()
  const { updateBilling } = useUpdateBilling()
  const inviteMode = isInviteMode()

  const handleBillingSetup = async (tier, premiumDetails) => {
    const updates = tier === 'free'
      ? { active_tier_code: 'free', total_credits: 200, recurring_credits: null, recurring_price: null, recurring_frequency: null }
      : {
          active_tier_code: 'premium',
          total_credits: 200,
          recurring_credits: 25 * premiumDetails.price,
          recurring_price: premiumDetails.price,
          recurring_frequency: premiumDetails.frequency,
        }

    const { error: billingError } = await updateBilling(resolvedUser.id, updates)
    if (billingError) setError(billingError.message)
    return billingError
  }

  const handleSelectFree = async () => {
    setLoading(true)
    setError(null)
    const billingError = await handleBillingSetup('free', null)
    if (!billingError) {
      markProfileComplete()
      navigate('/dashboard')
    }
    setLoading(false)
  }

  const handleSelectPremium = async ({ price, frequency }) => {
    setLoading(true)
    setError(null)
    const billingError = await handleBillingSetup('premium', { price, frequency })
    if (!billingError) {
      markProfileComplete()
      navigate('/dashboard')
    }
    setLoading(false)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!inviteMode && password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setLoading(true)
    setError(null)

    try {
      let resolved = user
      if (!resolved) {
        const { data } = await supabase.auth.getUser()
        resolved = data?.user ?? null
      }

      if (!resolved) {
        setError('No active session found. Please try signing in again.')
        return
      }

      if (!inviteMode) {
        const { error: updateError } = await supabase.auth.updateUser({ password })
        if (updateError) {
          setError(updateError.message)
          return
        }
      }

      const { error: profileError } = await createUserProfile({
        id: resolved.id,
        username,
        email: resolved.email,
      })
      if (profileError) {
        setError(profileError.message)
        return
      }

      const pendingTicket = sessionStorage.getItem('pendingInviteTicket')
      if (pendingTicket) {
        const result = await validateInviteTicket(pendingTicket)

        if (result.valid) {
          const { error: orgError } = await supabase.from('organization_users').insert({
            organization_id: result.organizationId,
            user_id: resolved.id,
            role: result.role,
          })

          if (!orgError) {
            await supabase.from('invite_ticket_activations').insert({
              ticket_id: pendingTicket,
              activated_by: resolved.id,
            })
          }
        } else {
          setInviteWarning(result.error)
        }

        sessionStorage.removeItem('pendingInviteTicket')
      }

      setResolvedUser(resolved)

      if (skipTier) {
        const { error: skipBillingError } = await updateBilling(resolved.id, {
          active_tier_code: 'free',
          total_credits: 200,
          recurring_credits: null,
          recurring_price: null,
          recurring_frequency: null,
        })
        if (!skipBillingError) {
          markProfileComplete()
          navigate('/dashboard')
        } else {
          setError(skipBillingError.message ?? 'Something went wrong')
        }
      } else {
        setStep(2)
      }
    } catch (err) {
      console.error('Unexpected error during setup:', err)
      setError(err.message ?? 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  if (step === 2) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 6.25, sm: 10 } }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {inviteWarning && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Your account was created, but the invite link could not be applied: {inviteWarning}
          </Alert>
        )}
        <TierSelection
          onSelectFree={handleSelectFree}
          onSelectPremium={handleSelectPremium}
          loading={loading}
        />
      </Container>
    )
  }

  return (
    <Container sx={{ py: { xs: 6.25, sm: 12.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            width: { xs: '96%', sm: '46%' },
          }}
        >
          <Typography variant="h4" component="h1">
            Set Up Your Account
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {inviteMode
              ? 'You\'ve been invited to TerraPlanner. Choose a username to complete your account setup.'
              : 'You\'ve been invited to TerraPlanner. Choose a username and password to complete your account setup.'}
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {inviteWarning && (
            <Alert severity="warning">
              Your account was created, but the invite link could not be applied: {inviteWarning}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            <TextField
              label="Username"
              placeholder="Your name"
              value={username}
              required
              inputProps={{ minLength: 3 }}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
            />
            {!inviteMode && (
              <>
                <TextField
                  type="password"
                  label="Password"
                  placeholder="Choose a password"
                  value={password}
                  required
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                />
                <TextField
                  type="password"
                  label="Confirm Password"
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  required
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                />
              </>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              {loading ? 'Setting up...' : 'Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  )
}
